import React, { useEffect, useMemo, useState } from 'react';
import { useStylesGeneral } from '../../../styles/StyleComponents';
import "../../../styles/site.css";
import { Button } from '@material-ui/core';
import AccordionCS from '../../../common/ComponentesCS/AccordionCS';
import MaterialTableCS from '../../../common/ComponentesCS/MaterialTableCS';
import ProductIntelisisConfigDialog from '../components/ProductIntelisisConfigDialog';
import { INTELISIS_ACCOUNT_FIELDS } from '../constants/intelisisConfig';

const tableActionButtonStyle = {
    backgroundColor: '#1976d2',
    color: 'white',
    padding: '5px 10px',
    fontSize: '12px',
    textTransform: 'none'
};

const tableActionButtonDisabledStyle = {
    ...tableActionButtonStyle,
    backgroundColor: '#bdbdbd'
};

const tableCenteredColumnStyle = {
    textAlign: 'center'
};

const tableCenteredActionContentStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    minWidth: '160px',
    textAlign: 'center'
};

const normalizeIntelisisOptions = (source) => {
    if (Array.isArray(source)) {
        return source;
    }

    if (Array.isArray(source?.data)) {
        return source.data;
    }

    return [];
};

const requiresIntelisisConfig = (product) => product?.altaIntelisis !== true;
const getProductKey = (idFactura, idArticulo) => `${idFactura}-${idArticulo}`;
const hasValue = (value) => value !== undefined && value !== null && `${value}`.trim() !== '';
const MAX_ARTICLE_CODE_LENGTH = 20;
const isWithinMaxLength = (value, maxLength) => !hasValue(value) || `${value}`.length <= maxLength;
const isValidArticleCode = (value) => /^[A-Za-z0-9]+$/.test(`${value ?? ''}`);
const isValidNumericValue = (value) => hasValue(value) && Number.isFinite(Number(value));
const normalizeBooleanValue = (value) => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value === 1;
    }

    const normalizedValue = `${value ?? ''}`.trim().toLowerCase();

    return ['true', '1', 'si', 'yes'].includes(normalizedValue);
};

const ProductDetails = (props) => {
    const {
        invoicesSelected,
        productsSelected,
        setProductsSelected,
        cuentasIntelisis = [],
        conceptosReteFuente = {},
        conceptosReteICA = {},
        intelisisLoading = false,
        validationSummary = {},
        showValidationErrors = false
    } = props;

    const [touchedFields, setTouchedFields] = useState({});
    const [configModalOpen, setConfigModalOpen] = useState(false);
    const [selectedProductConfig, setSelectedProductConfig] = useState(null);
    const [productConfigDraft, setProductConfigDraft] = useState(null);
    const classes = useStylesGeneral();
    const loading = false;

    const cuentasIntelisisOptions = useMemo(() => normalizeIntelisisOptions(cuentasIntelisis), [cuentasIntelisis]);
    const conceptosReteFuenteOptions = useMemo(() => normalizeIntelisisOptions(conceptosReteFuente), [conceptosReteFuente]);
    const conceptosReteICAOptions = useMemo(() => normalizeIntelisisOptions(conceptosReteICA), [conceptosReteICA]);
    const invalidProductsMap = validationSummary?.invalidProductsMap || {};

    const groupedProducts = useMemo(() => {
        const requiresIntelisisGroups = [];
        const noIntelisisGroups = [];

        Object.entries(productsSelected || {}).forEach(([idFactura, products]) => {
            const invoice = invoicesSelected.find((inv) => inv.idFactura.toString() === idFactura.toString());
            const intelisisRequiredProducts = (products || []).filter((product) => requiresIntelisisConfig(product));
            const noIntelisisRequiredProducts = (products || []).filter((product) => !requiresIntelisisConfig(product));

            if (intelisisRequiredProducts.length > 0) {
                requiresIntelisisGroups.push({
                    idFactura,
                    invoice,
                    products: intelisisRequiredProducts
                });
            }

            if (noIntelisisRequiredProducts.length > 0) {
                noIntelisisGroups.push({
                    idFactura,
                    invoice,
                    products: noIntelisisRequiredProducts
                });
            }
        });

        return {
            requiresIntelisisGroups,
            noIntelisisGroups
        };
    }, [productsSelected, invoicesSelected]);

    const markFieldTouched = (idFactura, idArticulo, field) => {
        const fieldKey = `${getProductKey(idFactura, idArticulo)}-${field}`;
        setTouchedFields((prevState) => ({
            ...prevState,
            [fieldKey]: true
        }));
    };

    const updateProductFields = (idFactura, idArticulo, updatedFields) => {
        if (!setProductsSelected) {
            return;
        }

        setProductsSelected((prevState) => {
            const updatedProducts = { ...prevState };

            if (updatedProducts[idFactura]) {
                updatedProducts[idFactura] = updatedProducts[idFactura].map((product) =>
                    product.idArticulo === idArticulo
                        ? {
                            ...product,
                            ...updatedFields
                        }
                        : product
                );
            }

            return updatedProducts;
        });
    };

    const updateProductField = (idFactura, idArticulo, field, value) => {
        updateProductFields(idFactura, idArticulo, { [field]: value });
    };

    const isProductInvalid = (rowData) => Boolean(invalidProductsMap[getProductKey(rowData.idFactura, rowData.idArticulo)]);

    const hasFieldError = (rowData, field) => {
        const productKey = getProductKey(rowData.idFactura, rowData.idArticulo);
        const touchedFieldKey = `${productKey}-${field}`;
        const productValidation = invalidProductsMap[productKey];

        return Boolean(
            productValidation?.missingFields?.includes(field)
            && (showValidationErrors || touchedFields[touchedFieldKey])
        );
    };

    const getMaxQuantity = (rowData) => {
        const maxQuantity = Number(rowData?.cantidad ?? 0);
        return Number.isFinite(maxQuantity) ? maxQuantity : 0;
    };

    const handleQuantityChange = (rowData, value) => {
        const trimmedValue = `${value}`.trim();

        if (trimmedValue === '') {
            updateProductField(rowData.idFactura, rowData.idArticulo, 'cantidadSeleccionada', '');
            return;
        }

        const numericValue = Number(trimmedValue);
        if (!Number.isFinite(numericValue)) {
            return;
        }

        const maxQuantity = getMaxQuantity(rowData);
        const normalizedValue = Math.min(Math.max(Math.floor(numericValue), 0), maxQuantity);

        updateProductField(rowData.idFactura, rowData.idArticulo, 'cantidadSeleccionada', normalizedValue);
    };

    const getProductConfigStatus = (rowData) => {
        const invalidProduct = invalidProductsMap[getProductKey(rowData.idFactura, rowData.idArticulo)];
        return {
            isComplete: !invalidProduct,
            missingFields: invalidProduct?.missingFields || []
        };
    };

    const handleOpenConfigurationModal = (rowData) => {
        setSelectedProductConfig({
            idFactura: rowData.idFactura,
            idArticulo: rowData.idArticulo,
            factura: rowData.factura,
            articulo: rowData.articulo
        });
        setConfigModalOpen(true);
    };

    const handleCloseConfigurationModal = () => {
        setConfigModalOpen(false);
        setSelectedProductConfig(null);
        setProductConfigDraft(null);
    };

    const handleConfigDraftChange = (field, value) => {
        setProductConfigDraft((prevState) => ({
            ...(prevState || {}),
            [field]: value
        }));
    };

    const handleConfigQuantityChange = (value) => {
        const trimmedValue = `${value}`.trim();

        if (trimmedValue === '') {
            handleConfigDraftChange('cantidadSeleccionada', '');
            return;
        }

        const numericValue = Number(trimmedValue);
        if (!Number.isFinite(numericValue)) {
            return;
        }

        const maxQuantity = getMaxQuantity(productConfigDraft || {});
        const normalizedValue = Math.min(Math.max(Math.floor(numericValue), 0), maxQuantity);
        handleConfigDraftChange('cantidadSeleccionada', normalizedValue);
    };

    const handleSaveConfiguration = () => {
        if (!selectedProductConfig || !productConfigDraft) {
            return;
        }

        if (!isValidArticleCode(productConfigDraft.articulo)) {
            markFieldTouched(selectedProductConfig.idFactura, selectedProductConfig.idArticulo, 'articulo');
            return;
        }

        if (!isWithinMaxLength(productConfigDraft.articulo, MAX_ARTICLE_CODE_LENGTH)) {
            markFieldTouched(selectedProductConfig.idFactura, selectedProductConfig.idArticulo, 'articulo');
            return;
        }

        if (!isWithinMaxLength(productConfigDraft.nombreCorto, MAX_ARTICLE_CODE_LENGTH)) {
            markFieldTouched(selectedProductConfig.idFactura, selectedProductConfig.idArticulo, 'nombreCorto');
            return;
        }

        if (!isValidNumericValue(productConfigDraft.impuesto1)) {
            markFieldTouched(selectedProductConfig.idFactura, selectedProductConfig.idArticulo, 'impuesto1');
            return;
        }

        updateProductFields(selectedProductConfig.idFactura, selectedProductConfig.idArticulo, {
            articulo: productConfigDraft.articulo,
            cantidadSeleccionada: productConfigDraft.cantidadSeleccionada,
            descripcion: productConfigDraft.descripcion1,
            descripcion1: productConfigDraft.descripcion1,
            nombreCorto: productConfigDraft.nombreCorto,
            nombrecorto: productConfigDraft.nombreCorto,
            importe: productConfigDraft.importe,
            fabricante: productConfigDraft.fabricante,
            categoria: productConfigDraft.categoria,
            proveedor: productConfigDraft.proveedor,
            unidad: productConfigDraft.unidad,
            unidadCompra: productConfigDraft.unidadCompra,
            familia: productConfigDraft.familia,
            monedaPrecio: productConfigDraft.monedaPrecio,
            rama: productConfigDraft.rama,
            csmktCategoria: productConfigDraft.csmktCategoria,
            impuesto1: productConfigDraft.impuesto1,
            impuesto1Excento: Boolean(productConfigDraft.impuesto1Excento),
            fiscalIngresoTercero: Boolean(productConfigDraft.fiscalIngresoTercero),
            intelisis_cuenta: productConfigDraft.intelisis_cuenta,
            intelisis_cuenta2: productConfigDraft.intelisis_cuenta2,
            intelisis_cuenta3: productConfigDraft.intelisis_cuenta3,
            intelisis_cuenta4: productConfigDraft.intelisis_cuenta4,
            intelisis_cuenta5: productConfigDraft.intelisis_cuenta5,
            intelisis_reteFuente: productConfigDraft.intelisis_reteFuente,
            intelisis_reteICA: productConfigDraft.intelisis_reteICA,
            csProcesador: productConfigDraft.csProcesador ?? null,
            csSistemaOperativo: productConfigDraft.csSistemaOperativo ?? null
        });

        [
            'articulo',
            'descripcion1',
            'nombreCorto',
            'fabricante',
            'categoria',
            'proveedor',
            'unidad',
            'unidadCompra',
            'familia',
            'monedaPrecio',
            'rama',
            'csmktCategoria',
            'impuesto1',
            'impuesto1Excento',
            'fiscalIngresoTercero',
            ...INTELISIS_ACCOUNT_FIELDS.map(({ field }) => field),
            'reteFuente',
            'reteICA'
        ].forEach((field) => {
            markFieldTouched(selectedProductConfig.idFactura, selectedProductConfig.idArticulo, field);
        });

        handleCloseConfigurationModal();
    };

    useEffect(() => {
        if (!configModalOpen || !selectedProductConfig) {
            setProductConfigDraft(null);
            return;
        }

        const products = productsSelected?.[selectedProductConfig.idFactura] || [];
        const currentProduct = products.find((product) => product.idArticulo === selectedProductConfig.idArticulo);

        if (!currentProduct) {
            setProductConfigDraft(null);
            return;
        }

        const descripcion1 = currentProduct.descripcion1
            || currentProduct.Descripcion1
            || currentProduct.descripcion
            || currentProduct.Descripcion
            || '';
        const nombreCorto = currentProduct.nombreCorto
            || currentProduct.NombreCorto
            || currentProduct.nombrecorto
            || currentProduct.articulo
            || '';
        const impuesto1 = currentProduct.impuesto1
            ?? currentProduct.Impuesto1
            ?? '';
        const impuesto1Excento = normalizeBooleanValue(
            currentProduct.impuesto1Excento
            ?? currentProduct.Impuesto1Excento
            ?? currentProduct.impuesto1Exento
            ?? currentProduct.Impuesto1Exento
            ?? false
        );
        const fiscalIngresoTercero = normalizeBooleanValue(
            currentProduct.fiscalIngresoTercero
            ?? currentProduct.FiscalIngresoTercero
            ?? false
        );

        setProductConfigDraft({
            ...currentProduct,
            cantidadSeleccionada: currentProduct.cantidadSeleccionada ?? '',
            descripcion: descripcion1,
            descripcion1,
            nombreCorto,
            importe: currentProduct.importe ?? currentProduct.precio ?? '',
            fabricante: currentProduct.fabricante || '',
            categoria: currentProduct.categoria || '',
            proveedor: currentProduct.proveedor || currentProduct.Proveedor || '',
            unidad: currentProduct.unidad || '',
            unidadCompra: currentProduct.unidadCompra || '',
            familia: currentProduct.familia || '',
            monedaPrecio: currentProduct.monedaPrecio || '',
            rama: currentProduct.rama || '',
            csmktCategoria: currentProduct.csmktCategoria || currentProduct.CSMKTCategoria || '',
            impuesto1,
            impuesto1Excento,
            fiscalIngresoTercero,
            intelisis_cuenta: currentProduct.intelisis_cuenta || '',
            intelisis_cuenta2: currentProduct.intelisis_cuenta2 || '',
            intelisis_cuenta3: currentProduct.intelisis_cuenta3 || '',
            intelisis_cuenta4: currentProduct.intelisis_cuenta4 || '',
            intelisis_cuenta5: currentProduct.intelisis_cuenta5 || '',
            intelisis_reteFuente: currentProduct.intelisis_reteFuente || '',
            intelisis_reteICA: currentProduct.intelisis_reteICA || '',
            csProcesador: currentProduct.csProcesador ?? '',
            csSistemaOperativo: currentProduct.csSistemaOperativo ?? ''
        });
    }, [configModalOpen, productsSelected, selectedProductConfig]);

    const invoiceColumns = [
        {
            field: 'factura',
            title: 'Factura',
        },
        {
            field: 'fechaActivo',
            title: 'Fecha Activo',
        },
        {
            field: 'cliente',
            title: 'Cliente',
        },
    ];

    const getBaseProductColumns = ({
        descriptionEditable = true,
        priceEditable = true,
        includeConfigureAction = false
    } = {}) => {
        const columns = [
            {
                field: 'factura',
                title: 'Factura',
                render: (rowData) => `${rowData.idFactura} - ${rowData.factura}`
            },
            {
                field: 'articulo',
                title: 'Articulo',
                render: (rowData) => (
                    <div>
                        <div>{rowData.articulo}</div>
                        {isProductInvalid(rowData) && (
                            <div style={{ color: '#d32f2f', fontSize: '11px', fontWeight: 'bold', marginTop: '4px' }}>
                                Configuracion Intelisis incompleta
                            </div>
                        )}
                    </div>
                )
            },
            {
                field: 'cantidad',
                title: 'Cant. Total',
                render: (rowData) => {
                    // if cantidad !== cantidadPendiente, show cantidadPendiente else show cantidad
                    const cantidadPendiente = rowData.cantidadPendiente ?? rowData.cantidad;
                    const cantidadToShow = (rowData.cantidad !== cantidadPendiente) ? `${cantidadPendiente} (Pendiente)` : `${rowData.cantidad}`;

                    return (
                        <div>
                            {cantidadToShow}
                        </div>
                    );

                }
            },
            {
                field: 'cantidadSeleccionada',
                title: 'Cant. Seleccionada',
                render: (rowData) => {
                    const maxQuantity = getMaxQuantity(rowData);

                    return (
                        <div>
                            <input
                                type="number"
                                min="0"
                                max={maxQuantity}
                                step="1"
                                style={{ width: '80px', padding: '5px' }}
                                value={rowData.cantidadSeleccionada ?? ''}
                                onChange={(event) => handleQuantityChange(rowData, event.target.value)}
                            />
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                Max: {maxQuantity}
                            </div>
                        </div>
                    );
                }
            },
            {
                field: 'descripcion',
                title: 'Descripcion',
                render: (rowData) => descriptionEditable ? (
                    <input
                        type="text"
                        style={{ width: '200px', padding: '5px' }}
                        value={rowData.descripcion || ''}
                        onChange={(event) => updateProductField(rowData.idFactura, rowData.idArticulo, 'descripcion', event.target.value)}
                    />
                ) : (
                    <span>{rowData.descripcion || ''}</span>
                )
            },
            {
                field: 'precio',
                title: 'Precio Unitario',
                render: (rowData) => (
                    priceEditable ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                            $<input
                                type="number"
                                style={{ width: '90px', padding: '5px' }}
                                value={rowData.precioUnitario ?? ''}
                                onChange={(event) => updateProductField(rowData.idFactura, rowData.idArticulo, 'precioUnitario', event.target.value)}
                            />
                        </span>
                    ) : (
                        <span>${parseFloat(rowData.precioUnitario || 0).toFixed(2)}</span>
                    )
                )
            }
        ];

        if (includeConfigureAction) {
            columns.push({
                field: 'configurar',
                title: 'Configurar',
                sorting: false,
                headerStyle: tableCenteredColumnStyle,
                cellStyle: tableCenteredColumnStyle,
                render: (rowData) => {
                    const configStatus = getProductConfigStatus(rowData);

                    return (
                        <div style={tableCenteredActionContentStyle}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleOpenConfigurationModal(rowData)}
                                disabled={intelisisLoading}
                                style={intelisisLoading ? tableActionButtonDisabledStyle : tableActionButtonStyle}
                            >
                                Configurar
                            </Button>
                            <span style={{
                                fontSize: '11px',
                                fontWeight: 'bold',
                                color: configStatus.isComplete ? '#2e7d32' : '#d32f2f'
                            }}  >
                                {configStatus.isComplete ? 'Configuracion completa' : 'Configuracion pendiente'}
                            </span>
                        </div>
                    );
                }
            });
        }

        return columns;
    };

    const productColumnsWithIntelisis = getBaseProductColumns({
        descriptionEditable: false,
        priceEditable: true,
        includeConfigureAction: true
    });
    const productColumnsWithoutIntelisis = getBaseProductColumns({
        descriptionEditable: false,
        priceEditable: true,
        includeConfigureAction: false
    });

    const renderProductGroup = (groups, columns, emptyMessage) => {
        if (groups.length === 0) {
            return (
                <div style={{
                    padding: '15px',
                    border: '1px dashed #c4c4c4',
                    borderRadius: '8px',
                    color: '#666',
                    backgroundColor: '#fafafa'
                }}>
                    {emptyMessage}
                </div>
            );
        }

        return groups.map(({ idFactura, invoice, products }) => (
            <AccordionCS
                key={`accordion-${idFactura}-${columns.length}`}
                title={`Factura: ${invoice?.factura || idFactura} (${products.length} productos)`}
                descripcion={`Productos seleccionados de la factura ${invoice?.factura || idFactura}`}
                defaultExpanded={true}
            >
                <MaterialTableCS
                    data={products}
                    columns={columns}
                    title=""
                    pageSize={10}
                    showTitle={false}
                    selection={false}
                    actions={[]}
                    pageSizeOptions={[5, 10, 25, 50]}
                    toolbar={false}
                    search={true}
                    paging={true}
                    loading={loading}
                    autoHeight={true}
                />
            </AccordionCS>
        ));
    };

    return (
        <div className={`${classes.root}`} style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '20px' }}>
            {intelisisLoading && (
                <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', padding: '10px', borderRadius: '5px', margin: '10px 0' }}>
                    <span style={{ color: '#6c7b69' }}>Cargando datos de Intelisis...</span>
                </div>
            )}

            {!intelisisLoading && validationSummary?.requiresIntelisisCount > 0 && !validationSummary?.catalogsReady && (
                <div style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', padding: '10px', borderRadius: '5px', margin: '10px 0' }}>
                    <span style={{ color: '#721c24' }}>
                        No puedes continuar hasta que esten disponibles los catalogos de Intelisis para los productos que requieren configuracion.
                    </span>
                </div>
            )}

            {showValidationErrors && validationSummary?.invalidCount > 0 && (
                <div style={{ backgroundColor: '#fdecea', border: '1px solid #f5c6cb', padding: '10px', borderRadius: '5px', margin: '10px 0' }}>
                    <span style={{ color: '#b71c1c' }}>
                        Completa los datos Intelisis requeridos en {validationSummary.invalidCount} producto{validationSummary.invalidCount === 1 ? '' : 's'} para poder avanzar.
                    </span>
                </div>
            )}

            <div style={{ marginBottom: '20px' }}>
                <MaterialTableCS
                    data={invoicesSelected}
                    columns={invoiceColumns}
                    title=""
                    pageSize={5}
                    showTitle={false}
                    selection={false}
                    actions={[]}
                    pageSizeOptions={[5, 10, 25, 50]}
                    toolbar={false}
                    search={true}
                    paging={true}
                    loading={loading}
                    autoHeight={true}
                />
            </div>
            {
                groupedProducts.requiresIntelisisGroups.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#0d47a1' }}>
                                Productos que requieren configuracion Intelisis
                            </div>
                            <div style={{ color: '#546e7a', marginTop: '4px' }}>
                                Estos productos deben completar sus datos Intelisis antes de registrarse, incluyendo cuentas, retenciones y datos base del articulo.
                            </div>
                        </div>
                        {renderProductGroup(
                            groupedProducts.requiresIntelisisGroups,
                            productColumnsWithIntelisis,
                            // 'No hay productos que requieran configuracion Intelisis.'
                        )}
                    </div>)
            }
            {
                groupedProducts.noIntelisisGroups.length > 0 && (
                    <div>
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1b5e20' }}>
                                Productos creados en Intelisis
                            </div>
                            <div style={{ color: '#546e7a', marginTop: '4px' }}>
                                Estos productos no muestran ni requieren campos Intelisis porque ya tienen alta en Intelisis.
                            </div>
                        </div>
                        {renderProductGroup(
                            groupedProducts.noIntelisisGroups,
                            productColumnsWithoutIntelisis,
                            'No hay productos con alta Intelisis en esta seleccion.'
                        )}
                    </div>
                )}

            <ProductIntelisisConfigDialog
                open={configModalOpen}
                selectedProductConfig={selectedProductConfig}
                productConfigDraft={productConfigDraft}
                onClose={handleCloseConfigurationModal}
                onSave={handleSaveConfiguration}
                onDraftChange={handleConfigDraftChange}
                onQuantityChange={handleConfigQuantityChange}
                onTouchField={(field) => {
                    if (!selectedProductConfig) {
                        return;
                    }

                    markFieldTouched(selectedProductConfig.idFactura, selectedProductConfig.idArticulo, field);
                }}
                cuentasIntelisisOptions={cuentasIntelisisOptions}
                conceptosReteFuenteOptions={conceptosReteFuenteOptions}
                conceptosReteICAOptions={conceptosReteICAOptions}
                hasFieldError={hasFieldError}
                intelisisLoading={intelisisLoading}
                getMaxQuantity={getMaxQuantity}
            />
        </div>
    );
};

export default ProductDetails;
